import { cloneLayoutItem, type Compactor, verticalCompactor } from 'react-grid-layout';
import { useMemo, useState } from 'react';
import { dimensions, DRAG_HANDLE_CLASS, RL_GRID_ROW_HEIGHT } from 'venn-components';
import type { DropConfig } from 'react-grid-layout/core';
import type { PrintOrientationTypeEnum } from 'venn-api';
import { assertExhaustive } from 'venn-utils';

export const DROP_ITEM_ID = 'dropping_item';

export const useGridLayoutSettings = (
  printOrientation: PrintOrientationTypeEnum,
  canEditGrid: boolean,
  hasExpandedBlock: boolean,
  hasStaticBlock: boolean = false,
  pageUsableRows: number | undefined = undefined,
) => {
  const [isDraggingOver, setIsDraggingOver] = useState<boolean>(false);

  const { gridSettings } = dimensions[printOrientation];

  const droppingItem = useMemo(() => {
    switch (printOrientation) {
      case 'LANDSCAPE':
        return {
          i: DROP_ITEM_ID,
          w: 2,
          h: 6,
        };
      case 'PORTRAIT':
        return {
          i: DROP_ITEM_ID,
          w: 2,
          h: 8,
        };
      default:
        return assertExhaustive(printOrientation);
    }
  }, [printOrientation]);

  const dropConfig = useMemo(
    (): DropConfig => ({
      enabled: true,
      defaultItem: droppingItem,
    }),
    [droppingItem],
  );

  const gridConfig = useMemo(
    () => ({
      cols: gridSettings.noColumns,
      maxRows: hasExpandedBlock ? 9999 : (pageUsableRows ?? gridSettings.noRows),
      rowHeight: RL_GRID_ROW_HEIGHT,
      margin: gridSettings.margin,
      containerPadding: gridSettings.containerPadding,
    }),
    [gridSettings, hasExpandedBlock, pageUsableRows],
  );

  const dragConfig = useMemo(() => ({ enabled: canEditGrid, handle: `.${DRAG_HANDLE_CLASS}` }), [canEditGrid]);

  const compactor: Compactor = useMemo(
    () => ({
      type: hasStaticBlock ? 'vertical' : null,
      allowOverlap: false,
      preventCollision: false,

      compact(layout, cols) {
        const previewItem = layout.find((l) => l.i === DROP_ITEM_ID);

        setIsDraggingOver(!!previewItem);
        if (!previewItem) {
          return verticalCompactor.compact(layout, cols);
        }

        previewItem.w = droppingItem.w;
        const compactedLayout = verticalCompactor.compact(layout, cols);
        const itemsOnSameRow = compactedLayout
          .filter((l) => l.i !== DROP_ITEM_ID)
          .filter(({ y, h }) => y <= previewItem.y && previewItem.y < y + h);
        return compactedLayout.map((item) => {
          if (item.i !== DROP_ITEM_ID) {
            return item;
          }
          const newItem = cloneLayoutItem(item);
          const availableWidth = itemsOnSameRow.map(({ w }) => w).reduce((a, b) => a + b, 0);
          const remaining = gridSettings.noColumns - availableWidth;
          newItem.w =
            remaining >= droppingItem.w ? Math.min(remaining, gridSettings.maxDefaultColumns) : droppingItem.w;
          return newItem;
        });
      },
    }),
    [droppingItem, gridSettings.maxDefaultColumns, gridSettings.noColumns, hasStaticBlock],
  );

  return {
    compactor,
    isDraggingOver,
    gridConfig,
    dragConfig,
    dropConfig,
  };
};
